import { Head, Link, router, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  sitePath,
  siteLocationPath,
  siteLocationEnquiryPath,
  customerPortalHistoryPath,
} from "@/routes";

import EnquiryForm, { type Draft, type CustomerChoice } from "./enquiry-form";
import type { VehicleCatalogue } from "@/pages/vehicles/catalogue-fields";

type Offering = {
  id: string;
  name: string;
  description: string;
  price: string;
  currency: string;
  estimated_minutes: number;
  tax_mode: string;
  tax_label: string;
  tax_rate: string;
};
type Props = {
  site: {
    id: string;
    brand_name: string;
    headline: string;
    description: string;
  };
  locations: { id: string; name: string }[];
  location: {
    id: string;
    name: string;
    currency: string;
    description: string;
  } | null;
  offerings: Offering[];
  enquiry: boolean;
  draft?: Draft;
  customers?: CustomerChoice[];
  catalogue?: VehicleCatalogue;
  intake_path?: string;
  selected_offering?: Offering | null;
  sign_in_path?: string;
  customer_signed_in?: boolean;
};

export default function CompanyWebsite({
  site,
  locations,
  location,
  offerings,
  enquiry,
  selected_offering,
  sign_in_path,
  customer_signed_in,
  draft,
  customers = [],
  catalogue = {},
  intake_path,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = usePage().props.i18n.locale;
  return (
    <div className="bg-canvas text-foreground min-h-svh">
      <Head
        title={
          location ? `${location.name} · ${site.brand_name}` : site.brand_name
        }
      />
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-5 md:px-8">
          <Link
            href={sitePath(site.id)}
            className="min-w-0 text-xl font-semibold break-words hover:underline"
          >
            {site.brand_name}
          </Link>
          {locations.length > 0 && (
            <div className="w-full space-y-2 sm:w-64">
              <Label htmlFor="public-location">
                {t("website.choose_location")}
              </Label>
              <Select
                value={location?.id || ""}
                items={Object.fromEntries([
                  ["", t("website.all_locations")],
                  ...locations.map((item) => [item.id, item.name]),
                ])}
                onValueChange={(value) =>
                  router.visit(
                    value
                      ? siteLocationPath(site.id, value)
                      : sitePath(site.id),
                  )
                }
              >
                <SelectTrigger id="public-location" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t("website.all_locations")}</SelectItem>
                  {locations.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </header>
      <main
        id="main-content"
        tabIndex={-1}
        className="mx-auto max-w-6xl space-y-10 px-5 py-10 md:space-y-14 md:px-8 md:py-16"
      >
        <section className="space-y-5 rounded-lg bg-brand p-6 text-brand-foreground md:p-12">
          {location && (
            <p className="text-brand-muted font-medium break-words">
              {location.name}
            </p>
          )}
          <h1 className="max-w-3xl text-hero font-display break-words">
            {enquiry
              ? t("website.entry_title")
              : site.headline || site.brand_name}
          </h1>
          {site.description && (
            <p className="max-w-3xl text-brand-muted text-lg whitespace-pre-wrap break-words">
              {site.description}
            </p>
          )}
          {location?.description && (
            <p className="max-w-3xl text-brand-muted whitespace-pre-wrap break-words">
              {location.description}
            </p>
          )}
          {location && !enquiry && (
            <Button
              className="bg-brand-action text-brand-action-foreground hover:bg-brand-action-hover active:bg-brand-action-hover focus-visible:ring-brand-foreground"
              render={
                <Link href={siteLocationEnquiryPath(site.id, location.id)} />
              }
            >
              {t("website.enquire")}
            </Button>
          )}
        </section>
        {enquiry && location ? (
          <Card>
            <CardHeader>
              <CardTitle className="break-words">{location.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {selected_offering && (
                <p className="font-medium break-words">
                  {selected_offering.name}
                </p>
              )}
              <p className="text-muted-foreground max-w-2xl">
                {t("website.entry_help")}
              </p>
              {draft && intake_path && (
                <EnquiryForm
                  draft={draft}
                  customers={customers}
                  catalogue={catalogue}
                  action={intake_path}
                  offerings={offerings}
                />
              )}
              {customer_signed_in && (
                <p role="status">{t("website.signed_in")}</p>
              )}
              {!sign_in_path && !customer_signed_in && (
                <p role="status">{t("domains.auth_unavailable")}</p>
              )}
              <div className="flex flex-wrap gap-3">
                {customer_signed_in ? (
                  <Button render={<Link href={customerPortalHistoryPath()} />}>
                    {t("website.history")}
                  </Button>
                ) : (
                  sign_in_path && (
                    <Button render={<a href={sign_in_path} />}>
                      {t("website.sign_in")}
                    </Button>
                  )
                )}
                <Button
                  variant="outline"
                  render={
                    <Link href={siteLocationPath(site.id, location.id)} />
                  }
                >
                  {t("website.back")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : location ? (
          <section className="space-y-5" aria-labelledby="services-title">
            <h2 id="services-title" className="text-2xl font-semibold">
              {t("website.services")}
            </h2>
            {offerings.length === 0 && (
              <p className="text-muted-foreground">
                {t("website.empty_services")}
              </p>
            )}
            <div className="grid gap-5 md:grid-cols-2">
              {offerings.map((offering) => (
                <Card key={offering.id} className="min-w-0">
                  <CardHeader>
                    <CardTitle className="break-words">
                      {offering.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col gap-5">
                    {offering.description && (
                      <p className="text-muted-foreground whitespace-pre-wrap break-words">
                        {offering.description}
                      </p>
                    )}
                    <div className="space-y-1">
                      <p className="text-xl font-semibold">
                        {new Intl.NumberFormat(i18n.language, {
                          style: "currency",
                          currency: offering.currency,
                          currencyDisplay: "code",
                        }).format(Number(offering.price))}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {t("website.minutes", {
                          count: offering.estimated_minutes,
                        })}{" "}
                        · {t(`website.${offering.tax_mode}`)} (
                        {offering.tax_rate}%
                        {offering.tax_label ? ` · ${offering.tax_label}` : ""})
                      </p>
                    </div>
                    <Button
                      className="mt-auto self-start"
                      variant="outline"
                      render={
                        <Link
                          href={siteLocationEnquiryPath(site.id, location.id, {
                            offering_id: offering.id,
                          })}
                        />
                      }
                    >
                      {t("website.enquire_service")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ) : (
          <section className="space-y-5" aria-labelledby="locations-title">
            <h2 id="locations-title" className="text-2xl font-semibold">
              {t("website.locations")}
            </h2>
            {locations.length === 0 && (
              <p className="text-muted-foreground">
                {t("website.no_locations")}
              </p>
            )}
            <div className="grid gap-5 md:grid-cols-2">
              {locations.map((item) => (
                <Card key={item.id}>
                  <CardContent className="pt-6">
                    <Button
                      className="max-w-full whitespace-normal"
                      variant="outline"
                      render={
                        <Link href={siteLocationPath(site.id, item.id)} />
                      }
                    >
                      {item.name}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>
      <footer className="text-muted-foreground border-t px-5 py-6 text-center text-sm">
        {t("website.language", { language: t(`website.${locale}`) })}
      </footer>
    </div>
  );
}
